import React, { useState } from 'react';
import { Icons } from '../../shared/ui';
const { Zap: BoltIcon, Droplets: BeakerIcon, Plus: PlusIcon, Trash: TrashIcon, Edit: PencilIcon, Check: CheckIcon, X: XMarkIcon, QrCode: QrCodeIcon } = Icons;

/**
 * MeterMappingsEditor - Component for managing physical meter mappings for a room
 *
 * Schema: {"electric": {"meter_id": "E101", "initial_reading": 0}, "water": {"meter_id": "W101", "initial_reading": 0}}
 *
 * @param {Object} mappings - Current meter_mappings object
 * @param {Function} onChange - Callback when mappings change
 * @param {boolean} readOnly - If true, only display mode
 * @param {boolean} compact - Compact display mode
 * @param {Array} meteredServices - Building's metered services from services_config (overrides defaults)
 */
const MeterMappingsEditor = ({
  mappings = {},
  onChange,
  readOnly = false,
  compact = false,
  meteredServices = null
}) => {
  const [editingMeter, setEditingMeter] = useState(null);
  const [showAddMeter, setShowAddMeter] = useState(false);
  const [newMeter, setNewMeter] = useState({
    type: 'electric',
    meter_id: '',
    initial_reading: 0
  });

  // Ensure mappings is an object
  const meterMappings = typeof mappings === 'object' && mappings !== null ? mappings : {};

  // Icon/color mapping for known service types
  const serviceIcons = {
    electric: { icon: BoltIcon, color: 'yellow' },
    water: { icon: BeakerIcon, color: 'sky' },
  };

  // Build meter types dynamically from building's services_config or fallback to defaults
  const meterTypes = meteredServices && meteredServices.length > 0
    ? meteredServices.map(s => ({
        type: s.service_id,
        label: `${s.name} Meter`,
        icon: serviceIcons[s.service_id]?.icon || BoltIcon,
        color: serviceIcons[s.service_id]?.color || 'gray',
        unit: s.unit || 'unit'
      }))
    : [
        { type: 'electric', label: 'Electric Meter', icon: BoltIcon, color: 'yellow', unit: 'kWh' },
        { type: 'water', label: 'Water Meter', icon: BeakerIcon, color: 'sky', unit: 'm³' }
      ];

  // Get meter config by type
  const getMeterConfig = (type) => {
    return meterTypes.find(m => m.type === type) || { type, label: `${type} Meter`, icon: BoltIcon, color: 'gray', unit: 'unit' };
  };

  // Update a meter mapping
  const updateMeter = (type, field, value) => {
    if (readOnly) return;
    const updated = {
      ...meterMappings,
      [type]: {
        ...meterMappings[type],
        [field]: value
      }
    };
    onChange(updated);
  };

  // Remove a meter mapping
  const removeMeter = (type) => {
    if (readOnly) return;
    if (window.confirm(`Remove ${getMeterConfig(type).label} mapping?`)) {
      const updated = { ...meterMappings };
      delete updated[type];
      onChange(updated);
    }
  };

  // Add new meter
  const handleAddMeter = () => {
    if (!newMeter.meter_id) return;

    const updated = {
      ...meterMappings,
      [newMeter.type]: {
        meter_id: newMeter.meter_id,
        initial_reading: parseFloat(newMeter.initial_reading) || 0
      }
    };
    onChange(updated);
    setNewMeter({ type: meterTypes[0]?.type || 'electric', meter_id: '', initial_reading: 0 });
    setShowAddMeter(false);
  };

  // Get available meter types (not already mapped)
  const availableMeterTypes = meterTypes.filter(m => !meterMappings[m.type]);

  if (compact) {
    // Compact view
    return (
      <div className="space-y-1">
        <h4 className="text-xs font-medium text-stone-500 uppercase tracking-wide">Meters</h4>
        <div className="flex flex-wrap gap-2">
          {Object.entries(meterMappings).map(([type, data]) => {
            const config = getMeterConfig(type);
            const Icon = config.icon;
            const colorMap = { yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200', sky: 'bg-sky-50 text-sky-700 border-sky-200', gray: 'bg-stone-50 text-stone-700 border-stone-200' };
            const colorClass = colorMap[config.color] || colorMap.gray;
            return (
              <div
                key={type}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium border ${colorClass}`}
              >
                <Icon size={14} />
                <span>{data.meter_id}</span>
              </div>
            );
          })}
          {Object.keys(meterMappings).length === 0 && (
            <span className="text-xs text-stone-400 italic">No meters assigned</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-stone-100 rounded-lg">
            <QrCodeIcon size={20} className="text-stone-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-stone-800">Meter Mappings</h3>
            <p className="text-xs text-stone-500">Physical meter IDs for this room</p>
          </div>
        </div>
        {!readOnly && availableMeterTypes.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setNewMeter({ ...newMeter, type: availableMeterTypes[0].type });
              setShowAddMeter(!showAddMeter);
            }}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors"
          >
            <PlusIcon size={16} />
            Add Meter
          </button>
        )}
      </div>

      {/* Add Meter Form */}
      {showAddMeter && !readOnly && availableMeterTypes.length > 0 && (
        <div className="mb-4 p-4 bg-white rounded-lg border border-stone-200 space-y-3">
          <h4 className="text-sm font-semibold text-stone-700">Add Meter Mapping</h4>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Meter Type</label>
              <select
                value={newMeter.type}
                onChange={(e) => setNewMeter({ ...newMeter, type: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              >
                {availableMeterTypes.map(m => (
                  <option key={m.type} value={m.type}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Meter ID</label>
              <input
                type="text"
                value={newMeter.meter_id}
                onChange={(e) => setNewMeter({ ...newMeter, meter_id: e.target.value.toUpperCase() })}
                placeholder="e.g., E101, W201"
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Initial Reading</label>
              <input
                type="number"
                value={newMeter.initial_reading}
                onChange={(e) => setNewMeter({ ...newMeter, initial_reading: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowAddMeter(false)}
              className="px-3 py-1.5 text-sm text-stone-600 hover:text-stone-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddMeter}
              disabled={!newMeter.meter_id}
              className="px-4 py-1.5 text-sm font-medium text-white bg-stone-600 rounded-lg hover:bg-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Meter
            </button>
          </div>
        </div>
      )}

      {/* Meters List */}
      <div className="space-y-2">
        {meterTypes.map((meterConfig) => {
          const data = meterMappings[meterConfig.type];
          const Icon = meterConfig.icon;
          const isEditing = editingMeter === meterConfig.type;

          if (!data) return null;

          return (
            <div
              key={meterConfig.type}
              className={`flex items-center gap-3 p-3 bg-white rounded-lg border transition-colors ${
                meterConfig.color === 'yellow' ? 'border-yellow-200 hover:border-yellow-300' :
                meterConfig.color === 'sky' ? 'border-sky-200 hover:border-sky-300' :
                'border-stone-200 hover:border-stone-300'
              }`}
            >
              {/* Icon */}
              <div className={`p-2 rounded-lg ${
                meterConfig.color === 'yellow' ? 'bg-yellow-100' :
                meterConfig.color === 'sky' ? 'bg-sky-100' : 'bg-stone-100'
              }`}>
                <Icon size={20} className={`${
                  meterConfig.color === 'yellow' ? 'text-yellow-600' :
                  meterConfig.color === 'sky' ? 'text-sky-600' : 'text-stone-600'
                }`} />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-stone-800">{meterConfig.label}</div>
                {isEditing && !readOnly ? (
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="text"
                      value={data.meter_id}
                      onChange={(e) => updateMeter(meterConfig.type, 'meter_id', e.target.value.toUpperCase())}
                      className="w-24 px-2 py-1 text-sm border border-stone-300 rounded focus:ring-2 focus:ring-teal-500"
                      placeholder="Meter ID"
                    />
                    <span className="text-xs text-stone-400">|</span>
                    <input
                      type="number"
                      value={data.initial_reading}
                      onChange={(e) => updateMeter(meterConfig.type, 'initial_reading', parseFloat(e.target.value) || 0)}
                      className="w-24 px-2 py-1 text-sm border border-stone-300 rounded focus:ring-2 focus:ring-teal-500"
                      placeholder="Initial"
                      min="0"
                      step="0.01"
                    />
                    <span className="text-xs text-stone-500">{meterConfig.unit}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`px-2 py-0.5 text-xs font-mono font-bold rounded ${
                      meterConfig.color === 'yellow' ? 'bg-yellow-50 text-yellow-700' :
                      meterConfig.color === 'sky' ? 'bg-sky-50 text-sky-700' :
                      'bg-stone-50 text-stone-700'
                    }`}>
                      {data.meter_id}
                    </span>
                    <span className="text-xs text-stone-500">
                      Initial: {data.initial_reading} {meterConfig.unit}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              {!readOnly && (
                <div className="flex items-center gap-1">
                  {isEditing ? (
                    <button
                      type="button"
                      onClick={() => setEditingMeter(null)}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                      title="Done editing"
                    >
                      <CheckIcon size={16} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingMeter(meterConfig.type)}
                      className="p-1.5 text-stone-400 hover:text-stone-600 hover:bg-stone-50 rounded-lg"
                      title="Edit"
                    >
                      <PencilIcon size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeMeter(meterConfig.type)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    title="Remove"
                  >
                    <TrashIcon size={16} />
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/* Empty state */}
        {Object.keys(meterMappings).length === 0 && (
          <div className="text-center py-6 text-stone-500">
            <QrCodeIcon size={32} className="mx-auto mb-2 text-stone-300" />
            <p className="text-sm">No meters assigned to this room</p>
            {!readOnly && availableMeterTypes.length > 0 && (
              <button
                type="button"
                onClick={() => setShowAddMeter(true)}
                className="mt-2 text-sm text-stone-600 hover:text-stone-800 font-medium"
              >
                + Add first meter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Info */}
      {Object.keys(meterMappings).length > 0 && (
        <div className="mt-4 text-xs text-stone-600 bg-stone-100/50 p-2 rounded-lg">
          💡 Meter IDs are used to match OCR readings from meter photos and for manual meter reading entry.
        </div>
      )}
    </div>
  );
};

export default MeterMappingsEditor;
